/**
 * Handles careers page functionality.
 * List all career items and provide filtering based on the available filter options (region, state, role, type and evergreen).
 */
class CareersPage extends HTMLElement {
    constructor() {
        super();

        this.filterOptions = this.querySelectorAll('select[name$="-options"]') ?? [];
        this.filterReset = this.querySelector(`input[name="filter-reset"]`);
        this.errorMessage = this.querySelector(`.career-no-item`);
        this.careerContainer = this.querySelector('.career-list');

        // Need `document` property to access elements by ID.
        this.careerDescriptionHeadings = document.getElementById('career-data-description-heading');
        this.metaobjectSuffix = 'gid://shopify/Metaobject/';

        // Filter options
        this.stateOptions = this.querySelector("[name='state-options']");
        this.regionOptions = this.querySelector("[name='region-options']");
        this.roleOptions = this.querySelector("[name='role-options']");

        this.setupEventListeners();
    }

    connectedCallback() {
        this.fetchCareersListing();
    }

    setupEventListeners() {
        this.filterOptions.forEach(option => option.addEventListener('change', () => this.filterCareerList()));
        this.filterReset?.addEventListener('click', () => this.resetFilter());
    }

    /**
     * Provides list of all careers via storefront API call.
     *
     * @return {Promise<void>}
     */
    async fetchCareersListing() {
        let hasNextPage = true;
        const params = new URL(document.location.toString()).searchParams;
        const showEvergreen = params.get('evergreen') && params.get('evergreen').toLowerCase() === 'true';

        let careersDisplayed = false;
        let startingCursor = null;

        const careerHeadings = this.getCareerHeadings();

        while (hasNextPage) {
            const response = await this.getCareers(startingCursor);

            const stateOption = [], regionOption = [], roleOption = [];

            document.querySelector('.careers-loading-overlay').classList.add('hide');

            response?.data?.metaobjects?.edges.forEach(career => {
                if (!career?.node) return;

                career = career.node;

                // If the type is available on the URL then all careers to be displayed must match it.
                if (params.get('type') &&
                    params.get('type').toLowerCase() !== career?.fields.find(field => field.key === 'type')?.value?.toLowerCase()?.replace(/ /g, '_')) {
                    return;
                }

                // If the evergreen Query string is provided as a True value, only the evergreen careers must be displayed.
                const evergreen = career?.fields.find(field => field.key === 'evergreen')?.value;

                if (showEvergreen && evergreen.toLowerCase() !== 'true') return;
                else if (!showEvergreen && evergreen.toLowerCase() === 'true') return;

                careersDisplayed = true;

                const state = career?.fields.find(field => field.key === 'state')?.value;
                const region = career?.fields.find(field => field.key === 'region')?.value;
                const role = career?.fields.find(field => field.key === 'title')?.value;

                if (!stateOption.includes(state)) stateOption.push(state);
                if (!regionOption.includes(region)) regionOption.push(region);
                if (!roleOption.includes(role)) roleOption.push(role);

                this.createNewCareerDiv(career, careerHeadings);
            });

            this.provideFilterOption(stateOption, 'state');
            this.provideFilterOption(regionOption, 'region');
            this.provideFilterOption(roleOption, 'role');

            hasNextPage = response?.data?.metaobjects?.pageInfo?.hasNextPage;
            startingCursor = response?.data?.metaobjects?.pageInfo?.endCursor;
        }

        const careersErrorMessage = this.querySelector(`.career-no-item`);

        // Toggle the display of the careers error message based on the careersDisplayed flag.
        !!careersDisplayed ? careersErrorMessage.classList.add('hide') : careersErrorMessage.classList.remove('hide');

        this.handleUrlParamsFilter(params);
    }

    /**
     * Filters the careers based on the available URL params for region, state and role.
     */
    handleUrlParamsFilter(params) {
        try {
            if (!(params.get('state') || params.get('region') || params.get('role'))) return;

            // Depending on the script, filter the careers
            this.filterCareerList(params.get('state'), params.get('region'), params.get('role'));

            // Auto-select the provided region on the filter list
            ['state', 'region', 'role'].forEach(filterItem => {
                if (!params.get(filterItem)) return;

                const filterElement = this.querySelector(`select[name="${filterItem}-options"]`);

                if (!filterElement) return;

                // Select the provided region on the filter list.
                Array.from(filterElement.options).some(option => {
                    if (option.value === params.get(filterItem)) {
                        option.selected = true;
                        return true;
                    }

                    return false;
                });
            });
        } catch (e) {
            console.log(e)
        }
    }

    /**
     * Provides the heading for the careers as they need to be styled bold.
     */
    getCareerHeadings() {
        const inputElement = this.careerDescriptionHeadings;

        // Parse the value attribute to extract the array
        const valueAsString = inputElement.innerHTML; // Get the value as a string

        return JSON.parse(valueAsString);
    }

    /**
     * Provides the filter options for the careers.
     * The type must be either state, region or role.
     */
    provideFilterOption(filterOptions = [], type) {
        if (!filterOptions.length) return;

        const selectElement = this.querySelector(`select[name="${type}-options"]`);

        if (!selectElement) return;

        // Append the given filter options (if not available) on the select element.
        filterOptions.forEach(option => {
            if (type === 'region') {
                option = this.querySelector(`input[name="region-data-${option.replace(this.metaobjectSuffix, '')}"]`)?.value;
            }

            const optionValue = option?.trim()?.replace(/ /g, '-');
            const optionExists = Array.from(selectElement.options).some(option => option.value?.toLowerCase() === optionValue?.toLowerCase());

            if (optionExists) return;

            const optionElement = this.createHtmlElement('option');
            optionElement.value = optionValue?.toLowerCase();
            optionElement.textContent = option;
            selectElement.appendChild(optionElement);
        });
    }

    /**
     * Fetches the careers from the Storefront API.=
     *
     * @return {Promise<void>}
     */
    async getCareers(startCursor = null) {
        const credentials = this.getCredentials();

        const response = await fetch(credentials.storefront.url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': credentials.storefront.access_token
            },
            body: JSON.stringify({ query: this.getGraphQLQuery(startCursor) })
        })

        return response.json();
    }

    /**
     * Returns the credentials for the Storefront API.
     * TODO: move this to a separate service class.
     *
     * @return {{storefront: {access_token: string, url: string}}}
     */
    getCredentials() {
        return {
            storefront: {
                url: this.querySelector(`input[name="storefront-api-url"]`).value,
                access_token: this.querySelector(`input[name="storefront-api-access-token"]`).value
            }
        }
    }

    /**
     * Returns the GraphQL query to fetch the careers.
     * Provides the first 25 careers sorted by created_at in descending order (provides the newer ones first).
     * TODO: move this to a separate service class.
     *
     * @param startCursor
     * @return {string}
     */
    getGraphQLQuery(startCursor = null) {
        const queryParameter = `first: 25, type: "career", sortKey:"created_at", reverse: true` + (startCursor ? `, after: "${startCursor}"` : "");

        return `
        query {
            metaobjects(${queryParameter}) {
                edges {
                    node {
                        id
                        type
                        handle
                        fields {
                            key
                            value
                        }
                    }
                }
                pageInfo {
                    hasNextPage
                    hasPreviousPage
                    startCursor
                    endCursor
                }
            }
        }`;
    }

    /**
     * Creates a new career div and appends it to the careers' container.
     *
     * @param {Object} careerInfo
     * @param careerHeadings
     */
    createNewCareerDiv(careerInfo, careerHeadings) {
        const getFieldValue = (key) => careerInfo.fields.find(field => field.key === key)?.value;

        const handle = careerInfo.handle;
        const title = getFieldValue('title');
        const description = getFieldValue('description');
        const url = getFieldValue('url');
        const address = getFieldValue('address');
        const suburb = getFieldValue('suburb');
        const regionId = getFieldValue('region');
        const state = getFieldValue('state');

        const region = document.querySelector(`input[name="region-data-${regionId?.replace(this.metaobjectSuffix, '')}"]`).value;

        const newCareer = this.createHtmlElement('div', 'career-item');
        this.addClassFromText(newCareer, state?.trim().toLowerCase());
        this.addClassFromText(newCareer, region?.trim().toLowerCase());
        this.addClassFromText(newCareer, title?.trim().toLowerCase());
        newCareer.id = `career-item-${handle}`;
        newCareer.addEventListener('click', () => this.toggleDescription(`career-item-${handle}`));

        // Career heading
        const jobTopic = this.createHtmlElement('div', 'job-topic');
        jobTopic.innerHTML = this.getDirectionSvg();
        newCareer.appendChild(jobTopic);

        const jobTitle = this.createHtmlElement('div', 'job-title', `${suburb} - ${title}`);
        jobTopic.appendChild(jobTitle);

        // Address
        const jobLocation = this.createHtmlElement('div', 'job-location', null, { 'data-name': 'job-location' });
        jobTopic.appendChild(jobLocation);

        const jobLocationAddress = this.createHtmlElement('div', null, address, { 'data-name': 'address-name' });
        jobLocation.appendChild(jobLocationAddress);

        const jobLocationRegion = this.createHtmlElement('div');
        const regionLabel = this.createHtmlElement('span', null, region, { 'data-name': 'region-name' });
        jobLocationRegion.appendChild(regionLabel);

        if (state) {
            const stateLabel = this.createHtmlElement('span', null, `, ${state}`, { 'data-name': 'state-name' });
            jobLocationRegion.appendChild(stateLabel);
        }

        jobLocation.appendChild(jobLocationRegion);

        const jobRole = this.createHtmlElement('label', 'job-role', title?.replace(/ /g, '-'), { 'data-name': 'job-role' });
        newCareer.appendChild(jobRole);

        // Description
        const jobDescription = this.createHtmlElement('div', 'job-description');
        jobDescription.id = `career-item-${handle}-description`;
        jobDescription.innerHTML = this.convertToHtmlEntities(JSON.parse(description), careerHeadings);

        // Apply now button on the description
        const contactMail = this.createHtmlElement('div');
        contactMail.classList.add('apply-link');
        const anchorTag = this.createHtmlElement('a', null, 'APPLY HERE', { href: url, target: '_blank' });
        contactMail.appendChild(anchorTag);
        jobDescription.appendChild(contactMail);
        newCareer.appendChild(jobDescription);

        this.careerContainer.appendChild(newCareer);
    }

    /**
     * Toggles description visibility.
     *
     * @param careerContentId
     */
    toggleDescription(careerContentId) {
        const careerContent = document.getElementById(careerContentId + '-description');

        if (careerContent.classList.contains('open-description')) {
            careerContent.classList.remove('open-description');
            document.getElementById(careerContentId).querySelector('svg').classList.remove('svg-rotate');
        } else {
            careerContent.classList.add('open-description');
            document.getElementById(careerContentId).querySelector('svg').classList.add('svg-rotate');
        }
    }

    /**
     * SVG providing visibility over the description of the career (by providing visual cues whether the description has been opened or not).
     *
     */
    getDirectionSvg() {
        return `<svg role="presentation" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" class="feather feather-chevron-down">
                <polyline points="6 9 12 15 18 9"/>
            </svg>`;
    }

    /**
     * Creates a new HTML element with the given class name and attributes.
     *
     * @param tag
     * @param className
     * @param textContent
     * @param attributes
     * @return {*}
     */
    createHtmlElement(tag, className = null, textContent = null, attributes = {}) {
        const element = document.createElement(tag);

        if (className) element.classList.add(...className.split(' '));
        if (textContent) element.textContent = textContent;

        Object.keys(attributes).forEach(attr => element.setAttribute(attr, attributes[attr]));

        return element;
    }

    /**
     * Adds a class to the given element based on the given text.
     *
     * @param element
     * @param text
     */
    addClassFromText(element, text) {
        if (text) element.classList.add(text.replace(/ /g, '-'));
    }

    /**
     * Converts the given JSON formatted rich text metafield data to HTML entities.
     *
     * @param data
     * @param careerHeadings
     * @return {string}
     */
    convertToHtmlEntities(data, careerHeadings = []) {
        let html = '';

        data.children.forEach(node => {
            switch (node.type) {
                case 'root':
                    html += this.convertToHtmlEntities(node, careerHeadings);
                    break;
                case 'paragraph':
                    html += careerHeadings.some(heading => node.children[0].value.toLowerCase().includes(heading.toLowerCase())) ?
                        `<p><strong>${this.htmlEntities(node.children[0].value)}</strong></p>` : `<p>${this.htmlEntities(node.children[0].value)}</p>`;
                    break;
                case 'list':
                    html += `<ul>`;
                    node.children.forEach(listItem => {
                        html += `<li>${this.htmlEntities(listItem.children[0].value)}</li>`;
                    });
                    html += `</ul>`;
                    break;
                default:
                    break;
            }
        });

        return html;
    }

    htmlEntities(str) {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    /**
     * Provides filtering of the career Item.
     */

    /**
     * Toggles display of the available selection option based on the given available options.
     * For the select element with the given selection name, if any of the option item is on the available options list
     * then it is displayed, otherwise hidden.
     *
     * @param selectionName
     * @param availableOptions
     */
    hideUnavailableSelectOption(selectionName, availableOptions) {
        Array.from(this.querySelector("[name='" + selectionName + "']") ?? []).forEach(function(element) {
            if (element.value === "all") {
                element.style.display = "block";
                return;
            }
            element.style.display = availableOptions.includes(element.value) ? "block" : "none";
        });
    }

    /**
     * Sets the filter selection list to have only the available set of sate, region and role.
     *
     * @param states
     * @param regions
     * @param roles
     */
    setAvailableFilterList(states, regions, roles) {
        const careersDisplayed = states.length || regions.length || roles.length;

        if (states.length) this.hideUnavailableSelectOption("state-options", states);
        if (regions.length) this.hideUnavailableSelectOption("region-options", regions);
        if (roles.length) this.hideUnavailableSelectOption("role-options", roles);

        !!careersDisplayed ? this.errorMessage.classList.add('hide') : this.errorMessage.classList.remove('hide');
    }

    /**
     * Filter the list of careers based on selected values.
     *
     * @param state
     * @param region
     * @param role
     */
    filterCareerList(state = null, region = null, role = null) {
        const selectedState = state || this.stateOptions.value;
        const selectedRegion = region || this.regionOptions.value;
        const selectedRole = role || this.roleOptions.value;

        let visibleCareersClasses = [];

        if (selectedState !== 'all') visibleCareersClasses.push(selectedState);
        if (selectedRegion !== 'all') visibleCareersClasses.push(selectedRegion);
        if (selectedRole !== 'all') visibleCareersClasses.push(selectedRole);

        let availableStates = [], availableRegions = [], availableRoles = [];

        // Loop through each of the career items listed on the page.
        Array.from(this.querySelectorAll('.career-item')).forEach(function (element) {
            // Get the available list of filter options.
            const jobRole = element.querySelector("[data-name='job-role']").innerHTML;
            const state = element.querySelector("[data-name='state-name']").innerHTML?.replace(', ', '');
            const region = element.querySelector("[data-name='region-name']").innerHTML;

            // If there are no class names set to be visible (set only when the filter is selected) then all career items must be visible.
            if (!visibleCareersClasses.length) {
                element.style.display = "block";
                // Since all career items are visible, we push all available roles, regions and states.
                availableRoles.push(jobRole.toLowerCase());
                availableRegions.push(region.toLowerCase());
                availableStates.push(state.toLowerCase());
                return;
            }
            // If there are specific filter options selected then display only the career info that matches the selected criteria.
            const currentElementClasses = element.classList.value.split(' ');
            let displayItem = false;

            for (const criteriaClass of visibleCareersClasses) {
                displayItem = currentElementClasses.includes(criteriaClass);

                // If the career Item doesn't contain the current class then ignore the rest of the check. Career Items must match all criteria to be displayed on the list.
                if (!displayItem) break;
            }

            element.style.display = displayItem ? "block" : "none";

            if (displayItem) {
                // Since the career item is meant to be displayed, store its role, region and state on the available list.
                availableRoles.push(jobRole.toLowerCase());
                availableRegions.push(region.toLowerCase());
                availableStates.push(state.toLowerCase());
            }
        });

        this.setAvailableFilterList([...new Set(availableStates)], [...new Set(availableRegions)], [...new Set(availableRoles)]);

        this.setParams(selectedState, selectedRegion, selectedRole);
    }

    /**
     * Resets all selected filter options.
     * Resetting filters will display all career items.
     */
    resetFilter() {
        const selectedState = this.stateOptions.value;
        const selectedRegion = this.regionOptions.value;
        const selectedRole = this.roleOptions.value;

        if (selectedState === "all" && selectedRegion === "all" && selectedRole === "all") return;

        this.stateOptions.selectedIndex = 0;
        this.regionOptions.selectedIndex = 0;
        this.roleOptions.selectedIndex = 0;

        // Re-filter the career selection to display all list items.
        this.filterCareerList();

        this.resetParams(['state', 'region', 'role']);
    }

    /**
     * Reset the URL params by removing the given list of params.
     *
     * @param paramsToRemove
     */
    resetParams(paramsToRemove = []) {
        const params = new URL(document.location.toString()).searchParams;

        paramsToRemove.forEach(param => {
            if (!params.get(param)) return;

            params.delete(param);
        });

        this.setUrlState(params.toString());
    }

    /**
     * Provides all selected state, region and role values to the URL.
     */
    setParams(state, region, role) {
        const params = new URL(document.location.toString()).searchParams;

        ['state', 'region', 'role'].forEach(filterOption => {
            let filterValue = '';

            switch (filterOption) {
                case 'state':
                    filterValue = state;
                    break;
                case 'region':
                    filterValue = region;
                    break
                case 'role':
                    filterValue = role;
                    break;
            }

            if (!filterValue || filterValue === 'all') {
                params.delete(filterOption);
                return;
            }

            params.set(filterOption, filterValue);
        });

        this.setUrlState(params.toString());
    }

    /**
     * Sets the URL parameters on the URL.
     *
     * @param parameterString
     */
    setUrlState(parameterString) {
        const currentUrl = new URL(document.location.toString());
        currentUrl.search = parameterString;

        // Update the browser's URL without reloading the page
        history.replaceState(null, '', currentUrl);
    }
}

if (!customElements.get('careers-page')) {
    customElements.define('careers-page', CareersPage);
}